import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/** Matches the StoredConversation shape in Chat.tsx so Chat can read sidebar history. */
export interface StoredMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  chips?: Array<{ id: string; name: string; flag?: string | null }>;
}

interface StoredConversation {
  id: string;
  title: string;
  messages: StoredMessage[];
  updatedAt: string;
}

/** MUST match Chat.tsx — single source of truth. */
export const CHAT_HISTORY_KEY = "hightekers.chat.history";
const SIDEBAR_CONV_ID = "sidebar-active";

// Count-free stored fallback. The live entity count is injected at DISPLAY
// time via the "seed_message" i18n key (see AIAssistantSidebar / Chat), so no
// stale number is ever persisted to localStorage.
const SEED: StoredMessage[] = [
  {
    id: "seed",
    role: "assistant",
    content:
      "Hi! Ask anything about Hightekers' entities — renewals, UBO, directors, addresses. I cite the entities I used.",
  },
];

function readMessages(): StoredMessage[] {
  if (typeof window === "undefined") return SEED;
  try {
    const raw = window.localStorage.getItem(CHAT_HISTORY_KEY);
    if (!raw) return SEED;
    const parsed = JSON.parse(raw) as StoredConversation[];
    if (!Array.isArray(parsed) || parsed.length === 0) return SEED;
    // Sidebar's conversation is always the first entry with id === SIDEBAR_CONV_ID.
    // If Chat created its own first, use that; sidebar will sync into it on next write.
    const sidebar = parsed.find((c) => c.id === SIDEBAR_CONV_ID) ?? parsed[0];
    return sidebar.messages.length > 0 ? sidebar.messages : SEED;
  } catch {
    return SEED;
  }
}

function writeMessages(messages: StoredMessage[]): void {
  if (typeof window === "undefined") return;
  try {
    const existing = (() => {
      try {
        const raw = window.localStorage.getItem(CHAT_HISTORY_KEY);
        if (!raw) return [] as StoredConversation[];
        const parsed = JSON.parse(raw) as StoredConversation[];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [] as StoredConversation[];
      }
    })();
    const sidebarConv: StoredConversation = {
      id: SIDEBAR_CONV_ID,
      title: messages.find((m) => m.role === "user")?.content.slice(0, 40) ?? "Sidebar conversation",
      messages: messages.slice(-50),
      updatedAt: new Date().toISOString(),
    };
    // Upsert: replace the sidebar entry at position 0; keep other conversations.
    const others = existing.filter((c) => c.id !== SIDEBAR_CONV_ID).slice(0, 24);
    window.localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify([sidebarConv, ...others]));
  } catch {
    /* ignore quota */
  }
}

/**
 * Shared hook for sidebar + full Chat page.
 * Returns [messages, setMessages, clearHistory].
 * Writes through to localStorage on every change and syncs across tabs.
 */
export function useChatHistory(): [
  StoredMessage[],
  Dispatch<SetStateAction<StoredMessage[]>>,
  () => void,
] {
  const [messages, setMessages] = useState<StoredMessage[]>(readMessages);

  useEffect(() => {
    writeMessages(messages);
  }, [messages]);

  // Cross-tab sync: another tab (e.g. the full /chat page) may update the key.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === CHAT_HISTORY_KEY) {
        setMessages(readMessages());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const clear = useCallback(() => setMessages(SEED), []);

  return [messages, setMessages, clear];
}
